import { useCallback } from 'react';
import {
  ColorValues,
  Flex,
  IconChevronRight,
  RemoteImage,
  Typography,
  convertHexToBackgroundColorKeys,
  convertHexToContentColorKeys,
  type TypographyVariant,
} from '@banhala/ui-kit';
import { isNil, isNotNil } from '../../../../utils';
import { HTMLText } from '../../../HTMLText';
import { useCountdown } from '../../../../hooks/useCountdown';
import { useOpenDeeplink } from '../../../../hooks';
import { useLogAnalyticsEvent } from '../../../../hooks/useLogAnalyticsEvent';
import type {
  DynamicElement,
  DynamicIconStyle,
  DynamicTextStyle,
  DynamicTimerStyle,
  HeadlineOneLineDynamicProps,
} from './types';

/**
 * 서버가 내려준 elements / right / timer 등을 동적으로 조합해서 렌더하는 헤더.
 * iOS `MetaFeedOneLineDynamicHeaderModel` / Android `HeadlineOneLineDynamic` 와 동일.
 *
 * - title: HTMLText (font 토큰)
 * - subtitle.elements: text/icon 인라인 (text 는 bg/cornerRadius 옵션, 둘 다 있으면 무시)
 * - timer: countdown HH:mm:ss + 옵션 아이콘
 * - caption: 아래 줄 부가 텍스트
 * - right: 우측 액션 (텍스트/아이콘 + 옵션 chevron + deeplink)
 */
export const HeadlineOneLineDynamic = ({
  header,
  screen,
}: HeadlineOneLineDynamicProps) => {
  const { openDeeplink } = useOpenDeeplink();
  const logAnalyticsEvent = useLogAnalyticsEvent();
  const { timeText: timerText } = useCountdown(header?.item.timer?.finished_at);

  const handlePressRight = useCallback(() => {
    if (isNil(header)) return;
    const { right } = header.item;
    if (isNil(right)) return;
    logAnalyticsEvent({
      name: 'CLICK_HEADLINE_SMALL_ONE_LINE_MORE',
      analytics: {
        ...header.logging.analytics,
        ...(screen != null && { SCREEN: screen }),
      },
      ably_only_log: header.logging.ably_only_log,
    });
    openDeeplink(right.deeplink);
  }, [header, screen, openDeeplink, logAnalyticsEvent]);

  if (isNil(header)) return null;

  const { title, subtitle, caption, timer, right, padding } = header.item;

  return (
    <Flex
      padding={{
        top: padding.top,
        bottom: padding.bottom,
        left: padding.left,
        right: padding.right,
      }}
      backgroundColor={'bg_always_white'}
    >
      <Flex.Row alignItems={'center'}>
        <Flex.Row
          flex={1}
          alignItems={'center'}
          gap={4}
          testID={`${header.logging.analytics?.LIST_ID}?VALUE=${title.text}`}
        >
          <HTMLText type={resolveFont(title.font)} color={'content_primary'}>
            {title.text}
          </HTMLText>
          {isNotNil(subtitle) && subtitle.elements.length > 0 && (
            <Flex.Row alignItems={'center'} gap={4}>
              {subtitle.elements.map((element, idx) => (
                <ElementView key={`sub-${idx}`} element={element} />
              ))}
            </Flex.Row>
          )}
          {isNotNil(timer) && isNotNil(timerText) && (
            <TimerRow timer={timer} timerText={timerText} />
          )}
        </Flex.Row>

        {isNotNil(right) && (
          <Flex.Row
            alignItems={'center'}
            gap={2}
            onPress={handlePressRight}
            padding={{ vertical: 4, left: 8 }}
          >
            {right.elements.map((element, idx) => (
              <ElementView key={`right-${idx}`} element={element} />
            ))}
            {right.trailing_chevron && (
              <IconChevronRight
                width={10}
                height={10}
                fill={ColorValues.gray60}
              />
            )}
          </Flex.Row>
        )}
      </Flex.Row>

      {isNotNil(caption) && (
        <Flex margin={{ top: 4 }}>
          <HTMLText
            type={resolveFont(caption.font)}
            color={'content_primary'}
          >
            {caption.text}
          </HTMLText>
        </Flex>
      )}
    </Flex>
  );
};

const ElementView = ({ element }: { element: DynamicElement }) => {
  if (isNotNil(element.text)) {
    return <ElementText text={element.text} />;
  }
  if (isNotNil(element.icon)) {
    return <ElementIcon icon={element.icon} />;
  }
  return null;
};

const ElementText = ({ text }: { text: DynamicTextStyle }) => {
  const bgColorKey = isNotNil(text.bg_color)
    ? (convertHexToBackgroundColorKeys(text.bg_color) ?? undefined)
    : undefined;
  const radius = text.corner_radius ?? 0;
  const hasBg = isNotNil(text.bg_color);

  if (hasBg) {
    return (
      <Flex
        backgroundColor={bgColorKey}
        backgroundColorValue={bgColorKey ? undefined : text.bg_color ?? undefined}
        borderRadius={radius}
        padding={{ horizontal: 6, vertical: 2 }}
      >
        <HTMLText type={resolveFont(text.font)} color={'content_primary'}>
          {text.text}
        </HTMLText>
      </Flex>
    );
  }

  return (
    <HTMLText type={resolveFont(text.font)} color={'content_primary'}>
      {text.text}
    </HTMLText>
  );
};

const ElementIcon = ({ icon }: { icon: DynamicIconStyle }) => (
  <RemoteImage uri={icon.icon_url} width={icon.width} height={icon.height} />
);

const TimerRow = ({
  timer,
  timerText,
}: {
  timer: DynamicTimerStyle;
  timerText: string;
}) => {
  const colorKey = convertHexToContentColorKeys(timer.color);
  return (
    <Flex.Row alignItems={'center'} gap={8}>
      {isNotNil(timer.icon) && <ElementIcon icon={timer.icon} />}
      <Typography
        type={resolveFont(timer.font)}
        color={colorKey ?? 'content_pink_primary'}
        rawColor={colorKey ? undefined : timer.color}
      >
        {timerText}
      </Typography>
    </Flex.Row>
  );
};

/**
 * 서버 font 문자열 → ui-kit Typography variant.
 * 서버가 보내는 token 이 그대로 valid 한 경우(예: 'h5','body3') 통과.
 * 그 외엔 안전한 기본값 'body3'.
 */
const VALID_FONT_TOKENS: ReadonlySet<TypographyVariant> = new Set<TypographyVariant>([
  'h1', 'h2', 'h3', 'h4', 'h5',
  'subtitle1', 'subtitle2', 'subtitle3', 'subtitle4', 'subtitle5', 'subtitle6', 'subtitle7',
  'body1', 'body2', 'body3', 'body4', 'body5',
]);

const resolveFont = (font: string): TypographyVariant => {
  return VALID_FONT_TOKENS.has(font as TypographyVariant)
    ? (font as TypographyVariant)
    : 'body3';
};
